import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import { StatesAndProvinces, useProductCenterApi } from 'api';
import { getStatesAndProvincesQuery } from 'api/queries';
import { mapStatesAndProvincesDtoToIdLabelCountry } from 'api/responses/models/Configuration/StatesAndProvincesDto';
import {
	ControlledAutocomplete,
	ControlledAutocompleteProps,
	defaultGetOptionLabel,
} from 'library/components/controls/form/ControlledAutocomplete';
import { Option } from 'library/models/Option';

export type StatesAndProvincesInputCustomProps = {
	countryCodeWithStatesAndProvinces: StatesAndProvinces;
};

type StatesAndProvincesInputProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<
	ControlledAutocompleteProps<Option<string>, TFieldValues, TName>,
	'options'
> &
	StatesAndProvincesInputCustomProps;

export const StatesAndProvincesInput = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	getOptionLabel = defaultGetOptionLabel,
	getOptionId,
	countryCodeWithStatesAndProvinces: statesAndProvinces,
	...props
}: StatesAndProvincesInputProps<TFieldValues, TName>): React.JSX.Element => {
	const { api } = useProductCenterApi();

	const { data } = useQuery(
		getStatesAndProvincesQuery(api, statesAndProvinces)
	);

	const options = useMemo(
		() => (data || []).map(mapStatesAndProvincesDtoToIdLabelCountry),
		[data]
	);

	return (
		<ControlledAutocomplete
			options={options}
			getOptionLabel={getOptionLabel}
			getOptionId={getOptionId}
			{...props}
		/>
	);
};
